import React, { useState, ChangeEvent } from "react";
import { CreditCard, Banknote, History, Check, Truck } from "lucide-react";
import CommonButtons from "src/shared/components/CommonButtons";
import { Order } from "src/shared/services/interfacev2";
import { toast } from "react-toastify";

interface Props {
  order: Order;
  onUpdateStatus: (status: string) => Promise<boolean>;
  onOpenPayment: (method?: string) => void;
  onOpenCancel: () => void;
}

const OrderActions: React.FC<Props> = ({
  order,
  onUpdateStatus,
  onOpenPayment,
  onOpenCancel,
}) => {
  const currentStatus = order?.orders?.[0]?.status || "Pending";
  const [selectedMethod, setSelectedMethod] = useState<string>("cash");
  const [isUpdatingStatus, setIsUpdatingStatus] = useState<boolean>(false);

  if (currentStatus === "Cancelled") {
    return (
      <div className="w-full flex justify-center py-3">
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-2.5 text-red-700 font-semibold text-sm">
          This order has been Cancelled.
        </div>
      </div>
    );
  }

  // Only Confirm and Completed in the dropdown
  const availableOptions: Record<string, string[]> = {
    Pending: ["Pending", "Confirm"],
    Confirm: ["Confirm"],
    "In-process": ["Confirm"],
    "Waiting for delivery": ["Confirm"],
    Completed: ["Completed"],
    Cancelled: ["Cancelled"],
  };

  const handleStatusChange = async (e: ChangeEvent<HTMLSelectElement>) => {
    const newStatus = e.target.value;
    if (newStatus === currentStatus) return;

    setIsUpdatingStatus(true);
    try {
      const success = await onUpdateStatus(newStatus);
      if (success) {
        toast.success(`Order status updated to "${newStatus}"`, {
          style: { borderRadius: "12px", background: "#1e293b", color: "#fff" },
        });
      }
    } catch (err) {
      console.error("Failed to update status", err);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Show payment methods when status is "Confirm" (user is about to complete)
  const showPaymentMethods = currentStatus === "Confirm";
  // Show completed info when order is fully completed
  const isCompleted = currentStatus === "Completed";
  const remainingAmount = Number(order?.orders?.[0]?.remainingAmount ?? 0);
  const completedPaymentMethod = order?.paymentHistory?.[order.paymentHistory.length - 1]?.paymentMethod || "N/A";

  const methods = [
    {
      key: "buy on credit",
      label: "Buy on Credit",
      icon: History,
      description: "Pay later",
    },
    {
      key: "card",
      label: "Card",
      icon: CreditCard,
      description: "Debit / Credit",
    },
    {
      key: "cash",
      label: "Cash",
      icon: Banknote,
      description: "Pay now",
    },
  ];

  return (
    <div className="w-full space-y-3 bg-white rounded-xl border border-gray-200 p-4">
      {/* Order Status Section */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-1 h-4 bg-slate-900 rounded-full" />
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">
            Order Status
          </h3>
        </div>

        <div className="relative">
          <select
            value={currentStatus}
            onChange={handleStatusChange}
            disabled={
              isUpdatingStatus ||
              currentStatus === "Delivered" ||
              currentStatus === "Cancelled"
            }
            className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 transition-all appearance-none cursor-pointer disabled:bg-slate-50 disabled:cursor-not-allowed"
          >
            {(availableOptions[currentStatus] || [currentStatus]).map((opt) => (
              <option
                key={opt}
                value={opt}
                className="text-slate-700 font-medium"
              >
                {opt}
              </option>
            ))}
          </select>

          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-slate-400">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2.5"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Payment Methods Section — shown when status is "Confirm" */}
      {showPaymentMethods && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-1 h-4 bg-slate-900 rounded-full" />
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">
              Payment Method
            </h3>
          </div>

          <div className="bg-gray-50 rounded-xl p-3">
            <div className="grid grid-cols-3 gap-2">
              {methods.map((method) => {
                const Icon = method.icon;
                const isActive = selectedMethod === method.key;

                return (
                  <button
                    key={method.key}
                    onClick={() => setSelectedMethod(method.key)}
                    className={`relative rounded-lg border p-2.5 transition-all duration-200 text-left
                      ${
                        isActive
                          ? "bg-slate-900 text-white border-transparent shadow-sm"
                          : "bg-white border-gray-200 hover:border-indigo-300"
                      }`}
                  >
                    {isActive && (
                      <div className="absolute top-1 right-1 w-3.5 h-3.5 flex items-center justify-center rounded-full bg-white/30">
                        <Check size={8} className="text-white" />
                      </div>
                    )}

                    <div className="flex items-center gap-2">
                      <div
                        className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0
                        ${isActive ? "bg-white/20 text-white" : "bg-gray-100 text-gray-600"}`}
                      >
                        <Icon size={14} />
                      </div>
                      <div className="flex flex-col leading-tight min-w-0">
                        <span
                          className={`text-xs font-semibold truncate ${isActive ? "text-white" : "text-gray-800"}`}
                        >
                          {method.label}
                        </span>
                        <span
                          className={`text-[10px] truncate ${isActive ? "text-white/80" : "text-gray-500"}`}
                        >
                          {method.description}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mt-3">
              <CommonButtons
                className="h-9 w-full"
                label="Complete Payment"
                onClick={() => onOpenPayment(selectedMethod)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Completed Section — shown when order is Completed */}
      {isCompleted && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-1 h-4 bg-emerald-600 rounded-full" />
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">
              Order Completed
            </h3>
          </div>

          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 space-y-2">
            {/* Payment method row */}
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                {completedPaymentMethod?.toLowerCase().includes("card") ? (
                  <CreditCard size={15} className="text-emerald-700" />
                ) : completedPaymentMethod?.toLowerCase().includes("credit") ? (
                  <History size={15} className="text-emerald-700" />
                ) : (
                  <Banknote size={15} className="text-emerald-700" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-[10px] uppercase tracking-wider text-emerald-600 font-semibold">Payment Method</p>
                <p className="text-sm font-bold text-emerald-800 capitalize">{completedPaymentMethod}</p>
              </div>
            </div>

            {/* Delivery row */}
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <Truck size={15} className="text-emerald-700" />
              </div>
              <div className="flex-1">
                <p className="text-[10px] uppercase tracking-wider text-emerald-600 font-semibold">Delivery Status</p>
                <span className="inline-flex items-center gap-1 mt-0.5 px-2 py-0.5 rounded-full bg-emerald-600 text-white text-xs font-bold">
                  <Check size={10} className="text-white" />
                  Completed
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Button */}
      {currentStatus !== "Delivered" && currentStatus !== "Cancelled" && (
        <div className="flex justify-end pt-1">
          <CommonButtons
            className="h-9 w-36"
            label="Cancel Order"
            variant="outlineDanger"
            onClick={onOpenCancel}
          />
        </div>
      )}
    </div>
  );
};

export default OrderActions;
